import { NETWORK_DESCRIPTIONS } from "QuotePage/NetworkDescriptions";
import { NETWORK_SIZE_CONSTANTS } from "QuotePage/NetworkSizeConstants";
import _ from "underscore";

import type { MedicalPlan } from "Utils/types";

export const deductibleOptions = [
  { label: "$0 to $1,000", value: { min: 0, max: 1000 } },
  { label: "$1,001 to $2,500", value: { max: 2500, min: 1001 } },
  { label: "$2,501 to $5,000", value: { max: 5000, min: 2501 } },
  { label: "$5,001 and above", value: { max: 100000, min: 5001 } },
];

export const networkOptions = (allPlans: Array<MedicalPlan>) => {
  // Currently limited to these network types, which covers all on-market plans in TX, OH, and VA
  // Should make sure we have other networks covered when/if this is released to a wider audience
  const loaded_plan_networks = _.uniq(allPlans.map((p) => p.plan_type)).sort();
  const preloaded_networks = ["PPO", "EPO", "POS", "HMO", "Indemnity"];
  // Use preloaded networks plus the networks pulled from loaded plans in case there are any discrepancies
  const all_networks = _.uniq(preloaded_networks.concat(loaded_plan_networks)).sort();
  const options = all_networks.map((val) => ({
    label: val,
    value: val,
    tooltip: NETWORK_DESCRIPTIONS[val],
  }));

  return options;
};

export const networkOptionsMock = (): Array<{
  label: string;
  value: string;
  tooltip: string;
}> => {
  const preloaded_networks = ["PPO", "EPO", "POS", "HMO", "HMP", "Indemnity"];
  const options = preloaded_networks.map((val) => ({
    label: val,
    value: val,
    tooltip: NETWORK_DESCRIPTIONS[val],
  }));

  return options;
};

export const networkSizeMock = (): Array<{
  label: string;
  value: number;
  tooltip: string;
}> => {
  const networkSizeOptions = [1, 2, 3, 4];
  const options = networkSizeOptions.map((val) => ({
    label: NETWORK_SIZE_CONSTANTS[val].label,
    value: val,
    tooltip: NETWORK_SIZE_CONSTANTS[val].tooltip,
  }));

  return options;
};

export const carrierOptions = (carriersInRegion: Array<string>): Array<{ label: string; value: string }> => {
  const options = carriersInRegion.map((val) => ({
    label: val,
    value: val,
  }));

  return options;
};

export const doctorVisitOptions = (allPlans: Array<MedicalPlan>) => {
  const max_doctor_costs = _.max(allPlans.map((p) => p.estimated_costs.pcp_visit));

  const options = [
    { label: "$0 to $30", value: { max: 30 } },
    { label: "$31 to $60", value: { max: 60, min: 31 } },
    {
      label: "Subject to deductible",
      value: { max: max_doctor_costs, min: 61 },
    },
  ];

  return options;
};

export const doctorVisitOptionsMock = [
  { label: "$0 to $30", value: { max: 30 } },
  { label: "$31 to $60", value: { max: 60, min: 31 } },
  {
    label: "Subject to deductible",
    value: { max: 999, min: 61 },
  },
];

export const specialistVisitOptionsMock = [
  { label: "$0 to $100", value: { max: 100 } },
  {
    label: "Subject to deductible",
    value: { max: 200, min: 100 },
  },
];

export const networkCoverageOptions = (county: string, stateName: string) => {
  const options = [
    {
      label: `${county} Area Coverage`,
      value: { min: 1, max: 4 },
    },

    {
      label: `${stateName} Coverage`,
      value: { min: 2, max: 4 },
    },

    { label: "Nationwide Coverage", value: { min: 3, max: 4 } },
  ];

  return options;
};

export const specialistVisitOptions = (allPlans: Array<MedicalPlan>) => {
  const max_doctor_costs = _.max(allPlans.map((p) => p.estimated_costs.specialist_visit));

  const options = [
    { label: "$0 to $100", value: { max: 100 } },
    {
      label: "Subject to deductible",
      value: { max: max_doctor_costs, min: 100 },
    },
  ];

  return options;
};

export const deductibleOptionsFormatted = [
  { min: 0, max: 1000 },
  { max: 2500, min: 1001 },
  { max: 5000, min: 2501 },
  { max: 100000, min: 5001 },
];

export const networkCoverageOptionsFormatted = [
  { min: 1, max: 4 },
  { min: 2, max: 4 },
  { min: 3, max: 4 },
];

export const doctorVisitOptionsFormatted = [{ max: 30 }, { max: 60, min: 31 }, { max: 999, min: 61 }];

export const specialistVisitOptionsFormatted = [{ max: 100 }, { max: 200, min: 100 }];

export const planTypesFormatted = [
  { term_value: "EPO" },
  { term_value: "HMO" },
  { term_value: "HMP" },
  { term_value: "Indemnity" },
  { term_value: "POS" },
  { term_value: "PPO" },
];

export const EMPTY_FILTERS = {
  average_monthly_premium: {},
  deductible: {},
  plan_type: {},
  network_size: {},
  id: {},
  carrier: {},
  hsa_eligible: {},
  generic_drug_copays: {},
  brand_drug_copays: {},
  all_drugs_subject_to_deductible: {},
  out_of_state: {},
  is_company_eligible_for_plan: {},
  saved_plans: {},
  dentalFilter: {
    carrier: {},
    deductible: {},
    annual_maximum: {},
    braces_covered: {},
    cavities_covered: {},
    crowns_covered: {},
    root_canals_covered: {},
    extraction_covered: {},
    gum_cleaning_covered: {},
    plan_type: {},
  },
  limitedValuePlansFilter: {
    plan_type: {
      include: ["mec_plans", "mvp_plans"],
    },
  },

  overflow_plan: {
    include: [false],
  },
};

export const limitedCoveragePlanTypes = [
  { label: "Minimum Essential Coverage", value: "mec_plans" },
  { label: "Minimum Value Plan", value: "mvp_plans" },
];
